import React, {useEffect, useState} from 'react';
import {Modal, Button, message, Table, Divider} from 'antd';
import {useQuery} from '@apollo/client';
import {
    GET_AVAILABLE_TASKS_FOR_TRANSFER,
    AvailableTaskForTransfer
} from '../../pickup-task/graphql/queries/getAvailableTasksForTransfer';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import DateFilterItem from '../../../delivery/components/DateFilterItem';

interface TransferTaskModalProps {
    visible: boolean;
    onClose: () => void;
    onConfirm: (targetTaskId: string) => void;
    currentTaskId: string;
    warehouseId: number;
    taskDate: string;
}

const TransferTaskModal: React.FC<TransferTaskModalProps> = ({
                                                                 visible,
                                                                 onClose,
                                                                 onConfirm,
                                                                 currentTaskId,
                                                                 warehouseId,
                                                                 taskDate
                                                             }) => {
    // 筛选状态
    const [filterStatus, setFilterStatus] = useState<string>('ALL');
    const [filterDate, setFilterDate] = useState<string>(taskDate);
    const {t} = useTranslation();
    const {data, loading, error} = useQuery(GET_AVAILABLE_TASKS_FOR_TRANSFER, {
        variables: {
            warehouseId,
            taskDate: filterDate,
            excludeTaskId: Number(currentTaskId)
        },
        skip: !visible || !warehouseId || !filterDate,
        fetchPolicy: 'network-only'
    });

    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

    useEffect(() => {
        if (!visible) {
            setSelectedTaskId(null);
            setFilterStatus('ALL');
            setFilterDate(taskDate);
        } else {
            // 当模态框打开时，使用传入的taskDate更新筛选日期
            setFilterDate(taskDate);
        }
    }, [visible, taskDate]);

    if (error) {
        message.error('Failed to load available tasks');
        return null;
    }

    // 根据状态筛选数据
    const filteredData = (data?.getAvailableTasksForTransfer || []).filter((task: AvailableTaskForTransfer) => {
        if (filterStatus === 'ALL') return true;
        return task.status === filterStatus;
    });

    const handleConfirm = () => {
        if (!selectedTaskId) {
            message.warning('Please select a target task');
            return;
        }
        onConfirm(selectedTaskId);
        onClose();
    };

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <div className="w-1 h-6 bg-purple-500 mr-3"></div>
                    <span className="text-lg font-medium">Add to Task</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={800}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    {t('pickup.appointment.transferTaskModal.cancel')}
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    onClick={handleConfirm}
                    disabled={!selectedTaskId}
                >
                    {t('pickup.appointment.transferTaskModal.ok')}
                </Button>
            ]}
        >
            {/* Filter conditions */}
            <div className="mb-4 grid grid-cols-2 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={String(t('pickup.appointment.transferTaskModal.taskStatus'))}
                        options={[
                            { label: String(t('components.input.all') || 'All'), value: 'ALL' },
                            { label: 'Draft', value: 'DRAFT' },
                            { label: 'Published', value: 'PUBLISHED' },
                            { label: 'In Progress', value: 'IN_PROGRESS' }
                        ]}
                        value={filterStatus}
                        onChange={(value: string) => setFilterStatus(value || 'ALL')}
                        className="w-full"
                        spanClassName="min-w-20 !font-medium"
                        showSearch
                    />
                </div>
                <div className="min-w-0">
                    <DateFilterItem
                        label={String(t('pickup.appointment.transferTaskModal.taskDate'))}
                        value={filterDate ? dayjs(filterDate) : null}
                        onChange={(date) => setFilterDate(date ? date.format('YYYY-MM-DD') : '')}
                        disabled
                        format="YYYY/MM/DD"
                        className="!flex-row !items-center"
                        spanClassName="min-w-20 !font-medium !mb-0"
                    />
                </div>
            </div>

            {/* Task table */}
            <Table
                loading={loading}
                dataSource={filteredData}
                pagination={false}
                size="small"
                rowKey="id"
                locale={{emptyText: 'No available tasks'}}
                rowClassName={(record: AvailableTaskForTransfer) =>
                    selectedTaskId === record.id.toString() ? 'bg-purple-50' : ''
                }
                onRow={(record: AvailableTaskForTransfer) => ({
                    onClick: () => setSelectedTaskId(record.id.toString()),
                    className: 'cursor-pointer hover:bg-gray-50'
                })}
                columns={[
                    {
                        title: '',
                        dataIndex: 'selection',
                        width: 40,
                        render: (_, record: AvailableTaskForTransfer) => (
                            <div
                                className="w-4 h-4 border-2 rounded flex items-center justify-center cursor-pointer"
                                style={{
                                    borderColor: selectedTaskId === record.id.toString() ? '#8b5cf6' : '#d1d5db',
                                    backgroundColor: selectedTaskId === record.id.toString() ? '#8b5cf6' : 'transparent'
                                }}
                            >
                                {selectedTaskId === record.id.toString() && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                            </div>
                        )
                    },
                    {
                        title: t('pickup.appointment.transferTaskModal.taskNumber'),
                        dataIndex: 'taskNumber',
                        key: 'taskNumber',
                        render: (text) => <span className="font-medium">{text}</span>
                    },
                    {
                        title: t('pickup.appointment.transferTaskModal.driver'),
                        dataIndex: 'driverName',
                        key: 'driverName',
                        align: 'left',
                        render: (text): string => text || '-'
                    },
                    {
                        title: t('pickup.appointment.transferTaskModal.vehicle'),
                        dataIndex: 'vehicleTypeName',
                        key: 'vehicleTypeName',
                        align: 'left',
                        render: (text: string): string => text || '-'
                    },
                    {
                        title: t('pickup.appointment.transferTaskModal.appointments'),
                        dataIndex: 'appointmentCount',
                        key: 'appointmentCount',
                        align: 'left'
                    },
                    {
                        title: t('pickup.appointment.transferTaskModal.quantity'),
                        dataIndex: 'quantityByUnit',
                        key: 'quantityByUnit',
                        align: 'left',
                        render: (text): string => text || '-'
                    },
                    {
                        title: t('pickup.appointment.transferTaskModal.loadRate'),
                        dataIndex: 'loadRate',
                        key: 'loadRate',
                        align: 'left',
                        render: (text) => <span className="font-medium">{text}</span>
                    }
                ]}
            />
        </Modal>
    );
};

export default TransferTaskModal;